/**
 * Notification Context
 * Manages notification state and real-time updates
 *
 * Integrates with Socket.io for real-time delivery,
 * shows Toast notifications via Sonner,
 * and manages Web Push subscriptions
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";
import { useSocket } from "./SocketContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";

const NotificationContext = createContext(null);

// API base - uses Vite proxy in development
const API_BASE = "/api/notifications";

export function NotificationProvider({ children }) {
  const { isAuthenticated, user } = useAuth();
  const { socket, isConnected } = useSocket();
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedRef = useRef(false);
  const seenNotifIdsRef = useRef(new Set());
  const activeTicketIdRef = useRef(null);
  const isInInboxRef = useRef(false);

  // Preload notification sound
  const notifSoundRef = useRef(null);
  useEffect(() => {
    const audio = new Audio("/audios/notification.mp3");
    audio.volume = 0.5;
    notifSoundRef.current = audio;
  }, []);

  const playNotificationSound = useCallback(() => {
    try {
      if (notifSoundRef.current) {
        notifSoundRef.current.currentTime = 0;
        notifSoundRef.current.play().catch(() => {});
      }
    } catch { /* ignore audio errors */ }
  }, []);

  // Push notifications
  const {
    isSupported: pushSupported,
    isSubscribed: pushSubscribed,
    permission: pushPermission,
    isLoading: pushLoading,
    error: pushError,
    subscribe: subscribePush,
    unsubscribe: unsubscribePush,
    toggleSubscription: togglePush,
    refreshPermission: refreshPushPermission,
  } = usePushNotifications();

  // ═══════════════════════════════════════════════════════════════
  // FETCH NOTIFICATIONS
  // ═══════════════════════════════════════════════════════════════

  const fetchNotifications = useCallback(
    async (options = {}) => {
      if (!isAuthenticated) return;

      try {
        setIsLoading(true);
        const params = new URLSearchParams();
        if (options.unreadOnly) params.append("unreadOnly", "true");
        if (options.limit) params.append("limit", options.limit);
        if (options.offset) params.append("offset", options.offset);

        const response = await fetch(`${API_BASE}?${params}`, {
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          setNotifications((prev) => {
            let local = prev.filter((n) => n.isLocal);
            // If fetching only unread, filter local ones too
            if (options.unreadOnly) {
              local = local.filter((n) => !n.readAt);
            }
            // Deduplicate in case needed, but mostly combine local with fetched
            return [...local, ...(data.notifications || [])];
          });
        }
      } catch (error) {
        console.error("[Notifications] Error fetching:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated],
  );

  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await fetch(`${API_BASE}/unread-count`, {
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count || 0);
      }
    } catch (error) {
      console.error("[Notifications] Error fetching count:", error);
    }
  }, [isAuthenticated]);

  // Initial load
  useEffect(() => {
    if (isAuthenticated && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      fetchNotifications({ unreadOnly: true });
      fetchUnreadCount();
    }

    if (!isAuthenticated) {
      hasLoadedRef.current = false;
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, fetchNotifications, fetchUnreadCount]);

  // ═══════════════════════════════════════════════════════════════
  // SOCKET LISTENER
  // ═══════════════════════════════════════════════════════════════

  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleNewNotification = (data) => {
      console.log("[Notifications] Received:", data);

      // Deduplicate: same notification can arrive via user room AND account room
      if (seenNotifIdsRef.current.has(data.id)) return;
      seenNotifIdsRef.current.add(data.id);

      // Cap dedup set to prevent unbounded growth during long sessions
      if (seenNotifIdsRef.current.size > 500) {
        const arr = [...seenNotifIdsRef.current];
        seenNotifIdsRef.current = new Set(arr.slice(-250));
      }

      // If the user is currently viewing this ticket, skip toast and don't
      // add to unread list — mark it as read immediately instead
      const isViewingTicket =
        data.ticketId && data.ticketId === activeTicketIdRef.current;

      if (isViewingTicket) {
        console.log(
          "[Notifications] Suppressed (user viewing ticket):",
          data.ticketId,
        );
        // Silently mark as read on the backend if it has a real id (non-transient)
        if (data.id && !data.transient && !String(data.id).startsWith("local-")) {
          fetch(`${API_BASE}/${data.id}/read`, {
            method: "PATCH",
            credentials: "include",
          }).catch(() => {});
        }
        return;
      }

      // ─── Transient notifications: toast/sound only, no bell/list ───
      if (data.transient) {
        // Check user sound preference
        const prefs = user?.notificationPreferences || {};
        const soundOn = prefs.soundEnabled !== false;

        // If user is in inbox and it's NEW_MESSAGE, play sound only (no toast)
        if (isInInboxRef.current && data.type === "NEW_MESSAGE") {
          if (soundOn) playNotificationSound();
          return;
        }

        // Show toast + sound for transient
        if (soundOn) playNotificationSound();
        showToast(data);
        return;
      }

      // ─── Persistent notifications: add to bell + toast ───
      // Add to list (at the beginning)
      setNotifications((prev) => {
        if (prev.some((n) => n.id === data.id)) return prev;
        return [data, ...prev];
      });

      // Increment unread count
      setUnreadCount((prev) => prev + 1);

      // Sound for persistent notifications (respects user preference)
      const persistPrefs = user?.notificationPreferences || {};
      if (persistPrefs.soundEnabled !== false) playNotificationSound();

      // Show toast notification
      showToast(data);
    };

    socket.on("notification:new", handleNewNotification);

    return () => {
      socket.off("notification:new", handleNewNotification);
    };
  }, [socket, isConnected]);

  // ═══════════════════════════════════════════════════════════════
  // TOAST NOTIFICATIONS
  // ═══════════════════════════════════════════════════════════════

  const showToast = useCallback(
    (notification) => {
      const { type, title, body, ticketId } = notification;

      // Strip HTML tags from toast body
      const cleanBody = (body || '').replace(/<[^>]*>/g, '').trim();

      // Determine navigation target
      const metadata = notification.metadata || {};
      const groupId = metadata.groupId;

      let action;
      if (ticketId) {
        action = {
          label: "Ver",
          onClick: () => {
            const clientId = user?.accountId;
            if (clientId) {
              navigate(`/internal/implementation/${clientId}/inbox?ticket=${ticketId}`);
            }
          },
        };
      } else if (groupId) {
        action = {
          label: "Ver",
          onClick: () => {
            const clientId = user?.accountId;
            if (clientId) {
              navigate(`/internal/implementation/${clientId}/inbox?tab=groups&group=${groupId}`);
            }
          },
        };
      }

      // Different toast styles based on type
      const toastOptions = {
        description: cleanBody,
        duration: 5000,
        action,
      };

      switch (type) {
        case "NEW_MESSAGE":
          toast.message(title, toastOptions);
          break;
        case "TICKET_ASSIGNED":
          toast.info(title, toastOptions);
          break;
        case "SLA_WARNING":
          toast.warning(title, toastOptions);
          break;
        case "INACTIVITY_ALERT":
          toast.warning(title, toastOptions);
          break;
        case "MENTION":
          toast.info(title, toastOptions);
          break;
        case "PLATFORM_ALERT":
          toast(title, toastOptions);
          break;
        case "account_update":
          toast.error(title, { ...toastOptions, duration: 8000 });
          break;
        default:
          toast(title, toastOptions);
      }
    },
    [navigate, user],
  );

  // ═══════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════

  const markAsRead = useCallback(async (notificationId) => {
    // Handle local notifications (don't exist on backend)
    if (String(notificationId).startsWith("local-")) {
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/${notificationId}/read`, {
        method: "PATCH",
        credentials: "include",
      });

      if (response.ok) {
        // Remove from local state (we show unread only)
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error("[Notifications] Error marking as read:", error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/mark-all-read`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        // Update local state
        setNotifications((prev) =>
          prev.map((n) => ({
            ...n,
            readAt: n.readAt || new Date().toISOString(),
          })),
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("[Notifications] Error marking all as read:", error);
    }
  }, []);

  const markTicketAsRead = useCallback(
    async (ticketId) => {
      try {
        const response = await fetch(`${API_BASE}/mark-ticket-read`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ ticketId }),
        });

        if (response.ok) {
          // Update local state
          setNotifications((prev) =>
            prev.map((n) =>
              n.ticketId === ticketId && !n.readAt
                ? { ...n, readAt: new Date().toISOString() }
                : n,
            ),
          );
          // Recalculate unread count
          fetchUnreadCount();
        }
      } catch (error) {
        console.error("[Notifications] Error marking ticket as read:", error);
      }
    },
    [fetchUnreadCount],
  );

  /**
   * Track which ticket the user is currently viewing.
   * When set, notifications for this ticket are auto-marked-read
   * and no toast is shown.
   */
  const setActiveTicketId = useCallback((ticketId) => {
    activeTicketIdRef.current = ticketId || null;
  }, []);

  /**
   * Track whether user is on the inbox page.
   * While in inbox, NEW_MESSAGE notifications play a sound
   * instead of showing a toast.
   */
  const setIsInInbox = useCallback((value) => {
    isInInboxRef.current = !!value;
  }, []);

  /**
   * Add a local notification (not persisted to backend)
   * Used for real-time events like template status updates
   */
  const addLocalNotification = useCallback((notification) => {
    const localNotif = {
      id: `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: notification.type || "PLATFORM_ALERT",
      title: notification.title,
      body: notification.body || notification.message,
      createdAt: new Date().toISOString(),
      readAt: null,
      isLocal: true, // Flag to identify local notifications
      ...notification,
    };

    // Deduplicate
    if (seenNotifIdsRef.current.has(localNotif.id)) return;
    seenNotifIdsRef.current.add(localNotif.id);

    // Add to list
    setNotifications((prev) => [localNotif, ...prev]);
    setUnreadCount((prev) => prev + 1);

    console.log("[Notifications] Added local notification:", localNotif.title);
  }, []);

  // ═══════════════════════════════════════════════════════════════
  // CONTEXT VALUE
  // ═══════════════════════════════════════════════════════════════

  const value = {
    // Notification state
    notifications,
    unreadCount,
    isLoading,

    // Notification actions
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    markTicketAsRead,
    showToast,
    addLocalNotification,
    setActiveTicketId,
    setIsInInbox,
    playNotificationSound,

    // Push notification state
    pushSupported,
    pushSubscribed,
    pushPermission,
    pushLoading,
    pushError,

    // Push notification actions
    subscribePush,
    unsubscribePush,
    togglePush,
    refreshPushPermission,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider",
    );
  }
  return context;
}

export default NotificationContext;
